/**
 * The optional self-explanation field (pedagogy spec §5.3.6, C-3): one sentence of the student's
 * own about the try they just made, "Bara du ser den". It sits in `RetryPanel` between the inputs
 * and the buttons (a wrong first try, still pending) and in `FeedbackPanel` above the folded
 * solution (the terminal state), both through their `selfExplanation` slot.
 *
 * The label is the item's authored prompt when it has one (it arrives with the solution, so only
 * the terminal panel can show it), else a generic one that depends on whether the try was wrong.
 * "Spara" saves once: the field then turns read-only with "Sparad". Text that was typed but not
 * saved is saved anyway when the field goes away ("Nästa", "Kontrollera igen", "Jag kör fast") or
 * when the solution is shown, and Ctrl/Cmd+Enter saves from inside the field; it never reveals or
 * submits anything. A failed save shows a toast and never blocks: the student can carry on.
 *
 * `attemptId` is the latest stored row of the item (the first try's id while the retry is
 * pending, the retry's id after it). The draft and the saved mark are remembered per attempt in
 * `selfExplanationStore.ts`, so a refresh keeps them and the field never posts a sentence twice.
 * Typing here never triggers the page's window shortcuts: `shortcutsBlocked` stands down for a
 * text field and for Ctrl/Cmd.
 */
import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { createContext, useContext, useEffect, useId, useRef, useState } from "react";
import type { Localized as LocalizedValue } from "@shared/types";
import { Localized } from "@client/components/Localized";
import { Button, Textarea, useToast } from "@client/components/ui";
import { useT } from "@client/i18n";
import { postSelfExplanation, practiceKeys } from "./api";
import { readDraft, sendingFor, sendSentence, writeDraft } from "./selfExplanationStore";
import type { QuestionResult } from "./session";

/** The server's cap on a sentence, after trimming (`selfExplanation.ts` on the server). */
export const SELF_EXPLANATION_MAX = 1000;

/**
 * Whether the worked solution has been opened in the panel around the field. `FeedbackPanel`
 * provides it; opening the solution ends the time for explaining it, so the field saves then.
 */
export const SolutionShownContext = createContext(false);

export interface SelfExplanationProps {
  /** The latest stored row of the item; the sentence is saved on it. */
  attemptId: string;
  /** The try just made was right: asks for a reason ("promptAfter") rather than the cause of a slip. */
  correct: boolean;
  /** The item's authored prompt; only the solution carries it. */
  prompt?: LocalizedValue | undefined;
}

function Field({ attemptId, correct, prompt }: SelfExplanationProps) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const solutionShown = useContext(SolutionShownContext);
  const headingId = useId();
  const [text, setText] = useState(() => readDraft(attemptId).text);
  const [saved, setSaved] = useState(() => readDraft(attemptId).saved);
  const [saving, setSaving] = useState(() => sendingFor(attemptId) !== undefined);

  // The latest values for `save`, which also runs from the unmount cleanup.
  const live = useRef({ text, saved, toast, t, queryClient });
  live.current = { text, saved, toast, t, queryClient };
  const mounted = useRef(true);
  const failureToasted = useRef(false);

  /** Saves what is typed, once. `quiet` is the unmount path: no state to update, no second toast. */
  async function save(quiet = false): Promise<void> {
    const { text: typed, saved: already } = live.current;
    const sentence = typed.trim();
    if (already || sentence === "") return;
    if (!quiet) setSaving(true);
    const ok = await sendSentence(attemptId, sentence, postSelfExplanation);
    if (ok) {
      void live.current.queryClient.invalidateQueries({ queryKey: practiceKeys.all });
      if (mounted.current) setSaved(true);
    } else if (!failureToasted.current) {
      failureToasted.current = true;
      live.current.toast.error(live.current.t("practice.selfExplain.failed"));
    }
    if (mounted.current) setSaving(false);
  }
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    mounted.current = true;
    // The same attempt's sentence is already on its way (the field that left just now posted it):
    // wait for it instead of offering a second save.
    const sending = sendingFor(attemptId);
    if (sending) {
      void sending.then((ok) => {
        if (!mounted.current) return;
        if (ok) setSaved(true);
        setSaving(false);
      });
    }
    return () => {
      mounted.current = false;
      // Text that was typed but not saved goes along when the field leaves ("Nästa" and the rest).
      if (!live.current.saved) void saveRef.current(true);
    };
  }, [attemptId]);

  // Opening the solution is the other moment the sentence is posted (spec §5.3.6).
  useEffect(() => {
    if (solutionShown) void saveRef.current();
  }, [solutionShown]);

  function onChange(value: string) {
    setText(value);
    writeDraft(attemptId, { text: value, saved: false });
  }

  const label = prompt ? (
    <Localized value={prompt} variant="inline" />
  ) : (
    t(correct ? "practice.selfExplain.promptAfter" : "practice.selfExplain.promptWrong")
  );

  return (
    <div
      role="group"
      aria-labelledby={headingId}
      className="mt-4 rounded-lg border border-ink-200 p-3 dark:border-ink-800"
    >
      <h3 id={headingId} className="mb-2 text-sm font-semibold text-ink-900 dark:text-ink-50">
        {t("practice.selfExplain.label")}
      </h3>
      <Textarea
        label={label}
        hint={t("practice.selfExplain.hint")}
        rows={2}
        maxLength={SELF_EXPLANATION_MAX}
        value={text}
        readOnly={saved}
        onChange={(event) => onChange(event.target.value)}
        onSubmitShortcut={() => void save()}
      />
      <div className="mt-2 flex min-h-8 items-center gap-3">
        {!saved && (
          <Button
            size="sm"
            variant="secondary"
            className="max-sm:min-h-11"
            onClick={() => void save()}
            loading={saving}
            disabled={text.trim() === ""}
          >
            {t("practice.selfExplain.save")}
          </Button>
        )}
        {/* Always in the page, so the change to "Sparad" is announced. */}
        <p
          role="status"
          className="flex items-center gap-1 text-sm text-green-800 dark:text-green-300"
        >
          {saved && (
            <>
              <Check size={14} aria-hidden="true" />
              {t("practice.selfExplain.saved")}
            </>
          )}
        </p>
      </div>
    </div>
  );
}

/** The field for one attempt; a new attempt id starts a fresh field (the old one saves on its way out). */
export function SelfExplanation(props: SelfExplanationProps) {
  return <Field key={props.attemptId} {...props} />;
}

/**
 * The field for a terminal result (`FeedbackPanel`'s slot): nothing without a stored attempt (a
 * session saved before the id existed, an open give-up). It asks about the latest try — the
 * retry's outcome when there was one — and uses the item's authored prompt from the solution.
 */
export function ResultSelfExplanation({ result }: { result: QuestionResult }) {
  if (!result.attemptId) return null;
  const latest = result.retried ?? result;
  return (
    <SelfExplanation
      attemptId={result.attemptId}
      correct={latest.correct === true}
      prompt={result.revealed?.selfExplanationPrompt}
    />
  );
}
